import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import {
  PackageCheck, Clock, ShieldAlert, CheckCircle2, XCircle, Boxes, CalendarClock, TrendingDown,
} from "lucide-react";

const STRIPES = {
  total: "bg-blue-500", wait: "bg-amber-500", quarantine: "bg-yellow-500",
  accepted: "bg-emerald-500", rejected: "bg-red-500", available: "bg-indigo-500", expiry: "bg-orange-500",
};

function Kpi({ stripe, icon: Icon, label, value, sub, onClick, testid }) {
  return (
    <Card data-testid={testid} onClick={onClick}
      className={`relative overflow-hidden p-5 ${onClick ? "cursor-pointer hover:shadow-md" : ""} transition-shadow`}>
      <div className={`kpi-stripe ${stripe}`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground font-medium">{label}</div>
          <div className="text-3xl font-heading font-extrabold mt-1">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <Icon className="w-6 h-6 text-muted-foreground" />
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-muted-foreground">Memuat...</div>;
  const c = s.status_counts;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Ringkasan penerimaan, mutu, dan stok bahan baku.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-total" stripe={STRIPES.total} icon={PackageCheck} label="Total Penerimaan" value={s.total_receipts} onClick={() => nav("/receipts")} />
        <Kpi testid="kpi-wait" stripe={STRIPES.wait} icon={Clock} label="Menunggu QC" value={c.MENUNGGU_PEMERIKSAAN} onClick={() => nav("/qc")} />
        <Kpi testid="kpi-quarantine" stripe={STRIPES.quarantine} icon={ShieldAlert} label="Karantina" value={c.KARANTINA} onClick={() => nav("/stock?b=quarantine")} />
        <Kpi testid="kpi-accepted" stripe={STRIPES.accepted} icon={CheckCircle2} label="Diterima" value={c.DITERIMA} />
        <Kpi testid="kpi-rejected" stripe={STRIPES.rejected} icon={XCircle} label="Ditolak" value={c.DITOLAK} onClick={() => nav("/stock?b=rejected")} />
        <Kpi testid="kpi-available" stripe={STRIPES.available} icon={Boxes} label="Stok Tersedia (qty)" value={s.available_qty} onClick={() => nav("/stock")} />
        <Kpi testid="kpi-quarantine-qty" stripe={STRIPES.quarantine} icon={ShieldAlert} label="Stok Karantina (qty)" value={s.quarantine_qty} />
        <Kpi testid="kpi-expiry" stripe={STRIPES.expiry} icon={CalendarClock} label="Mendekati Kedaluwarsa" value={s.near_expiry_count} onClick={() => nav("/stock?b=near_expiry")} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Bahan Mendekati Kedaluwarsa</h3>
          {s.near_expiry.length === 0 ? <div className="text-sm text-muted-foreground">Tidak ada bahan mendekati kedaluwarsa.</div> : (
            <div className="space-y-2">
              {s.near_expiry.map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2">
                  <div><span className="batch-chip">{l.internal_batch}</span> <span className="ml-2">{l.material_name}</span></div>
                  <div className="text-orange-600 font-medium">{fmtDate(l.expiry_date)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5">
          <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Peringatan Stok Minimum</h3>
          {s.low_stock.length === 0 ? <div className="text-sm text-muted-foreground">Semua stok di atas minimum.</div> : (
            <div className="space-y-2">
              {s.low_stock.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2">
                  <span>{l.material_name}</span>
                  <span className="text-red-600 font-medium">{l.available} / min {l.min_stock}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-heading font-bold mb-3">Penerimaan Terbaru</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b">
              <th className="py-2 font-medium">Batch Internal</th><th className="font-medium">Tgl</th>
              <th className="font-medium">Bahan</th><th className="font-medium">Pemasok</th>
              <th className="font-medium">Jumlah</th><th className="font-medium">Status</th>
            </tr></thead>
            <tbody>
              {s.recent.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-accent/50 cursor-pointer" onClick={() => nav(`/receipts/${r.id}`)}>
                  <td className="py-2"><span className="batch-chip">{r.internal_batch}</span></td>
                  <td>{fmtDate(r.tgl_terima)}</td><td>{r.material_name}</td><td>{r.supplier_name}</td>
                  <td>{r.jumlah} {r.unit}</td><td><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {s.recent.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">Belum ada penerimaan.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
